/**
 * Stack Layout
 * Vertical and horizontal stacking with consistent spacing
 */

import React from "react";
import { VStack as ChakraVStack, HStack as ChakraHStack } from "@chakra-ui/react";
import { SpacingToken, resolveSpacing, resolveSize, resolveRadius } from "design-system/foundation";
import { Spacing } from "design-system/foundation/Spacing";
import { CommonStyleProps, extractCommonProps } from "design-system/types/CommonProps";
import { useColorModeValue } from "design-system/hooks/useColorModeValue";

// Branded types for Stack
declare const __stack_direction_brand: unique symbol;
declare const __stack_align_brand: unique symbol;
declare const __stack_justify_brand: unique symbol;

export interface StackDirectionToken {
  readonly [__stack_direction_brand]: true;
  readonly _value: string;
}

export interface StackAlignToken {
  readonly [__stack_align_brand]: true;
  readonly _value: string;
}

export interface StackJustifyToken {
  readonly [__stack_justify_brand]: true;
  readonly _value: string;
}

// Factory functions
const createStackDirectionToken = (value: string): StackDirectionToken => ({ _value: value } as StackDirectionToken);

const createStackAlignToken = (value: string): StackAlignToken => ({ _value: value } as StackAlignToken);

const createStackJustifyToken = (value: string): StackJustifyToken => ({ _value: value } as StackJustifyToken);

export interface StackProps extends CommonStyleProps {
  children: React.ReactNode;
  direction?: StackDirectionToken;
  spacing?: SpacingToken;
  align?: StackAlignToken;
  justify?: StackJustifyToken;
  // Common React props
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  // Event handlers
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  // Accessibility
  "aria-label"?: string;
  role?: string;
}

function StackComponent(allProps: StackProps) {
  const {
    children,
    direction = stackTokens.direction.vertical,
    spacing = Spacing.medium,
    align = stackTokens.align.stretch,
    justify = stackTokens.justify.start,
    ...restProps
  } = allProps;

  // Extract and resolve common styling props
  const { cdsProps, remainingProps } = extractCommonProps(restProps);

  const resolvedSpacing = resolveSpacing(spacing);

  // Resolve common props
  const resolvedCommonProps: Record<string, string> = {};
  Object.entries(cdsProps).forEach(([key, value]) => {
    if (value && typeof value === "object" && "_spacingToken" in value) {
      // It's a radius token
      resolvedCommonProps[key] = resolveRadius(value as any);
    } else if (value && typeof value === "object" && "_value" in value) {
      // It's a spacing or length token
      if (key.includes("p") || key.includes("m") || key === "borderWidth") {
        resolvedCommonProps[key] = resolveSpacing(value as any);
      } else {
        resolvedCommonProps[key] = resolveSize(value as any);
      }
    } else if (value && typeof value === "object" && ("_semantic" in value || "_hue" in value)) {
      // It's a color token
      resolvedCommonProps[key] = useColorModeValue(value as any);
    }
  });

  const getAlignValue = (align: StackAlignToken) => {
    switch (align._value) {
      case "start": return "flex-start";
      case "end": return "flex-end";
      case "center": return "center";
      case "stretch": return "stretch";
      default: return "stretch";
    }
  };

  const getJustifyValue = (justify: StackJustifyToken) => {
    switch (justify._value) {
      case "start": return "flex-start";
      case "end": return "flex-end";
      case "center": return "center";
      case "between": return "space-between";
      case "around": return "space-around";
      case "evenly": return "space-evenly";
      default: return "flex-start";
    }
  };

  const commonProps = {
    spacing: resolvedSpacing,
    align: getAlignValue(align),
    justify: getJustifyValue(justify),
    ...resolvedCommonProps,
    ...remainingProps,
  };

  if (direction._value === "horizontal") {
    return (
      <ChakraHStack {...commonProps}>
        {children}
      </ChakraHStack>
    );
  }

  return (
    <ChakraVStack {...commonProps}>
      {children}
    </ChakraVStack>
  );
}

// Stack tokens
const stackTokens = {
  direction: {
    vertical: createStackDirectionToken("vertical"),
    horizontal: createStackDirectionToken("horizontal"),
  } as const,

  align: {
    start: createStackAlignToken("start"),
    center: createStackAlignToken("center"),
    end: createStackAlignToken("end"),
    stretch: createStackAlignToken("stretch"),
  } as const,

  justify: {
    start: createStackJustifyToken("start"),
    center: createStackJustifyToken("center"),
    end: createStackJustifyToken("end"),
    between: createStackJustifyToken("between"),
    around: createStackJustifyToken("around"),
    evenly: createStackJustifyToken("evenly"),
  } as const,
} as const;

// Public API - combines tokens and component
export const Stack = Object.assign(StackComponent, stackTokens);
